import { themeAtom } from "../../../shared/atoms";
import type { AdminOnboardingDeleteModalProps } from "../../models/admin-onboarding.models";
import { adminStyles } from "../../styles/admin.styles";
import { Dialog } from "@headlessui/react";
import { useAtomValue } from "jotai";
import { useRef } from "react";
import { twMerge } from "tailwind-merge";

// Confirms every loaded email record that blocks this vessel's next onboarding attempt.
const AdminOnboardingDeleteModal = ({
  rows,
  deleting,
  errorMessage,
  onClose,
  onConfirm,
}: AdminOnboardingDeleteModalProps) => {
  const theme = useAtomValue(themeAtom);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const vessel = rows[0];
  const emails = Array.from(new Set(rows.map((row) => row.EMAIL ?? "No email")));

  return (
    <Dialog
      as="div"
      open
      initialFocus={cancelRef}
      onClose={onClose}
      data-test="admin-onboarding-delete-modal"
      className="fixed inset-0 z-50"
    >
      <Dialog.Overlay className="fixed inset-0 bg-black/45" />
      <div className="fixed inset-0 flex items-center justify-center px-3 py-4">
        <Dialog.Panel
          className={twMerge(
            "max-h-[90vh] w-full max-w-lg overflow-y-auto bg-white px-5 py-5 shadow-2xl dark:bg-gray-800",
            theme.home.tailwind.textDefault,
          )}
        >
          <Dialog.Title className="text-xl font-semibold">Delete vessel onboarding?</Dialog.Title>
          <Dialog.Description className="mt-3 text-sm leading-6">
            Delete {rows.length} onboarding record{rows.length === 1 ? "" : "s"} for {vessel.VESSEL_NAME} (IMO{" "}
            {vessel.IMO_NUMBER ?? "N/A"}, vessel code {vessel.VESSEL_CODE ?? "N/A"})? This frees the vessel for the
            normal onboarding process to ingest it again using the current source email. This cannot be undone.
          </Dialog.Description>
          <div className="mt-3 text-sm">
            <p className="font-semibold">Emails on the records to delete</p>
            <ul data-test="admin-onboarding-delete-emails" className="mt-1 list-inside list-disc break-all">
              {emails.map((email) => (
                <li key={email}>{email}</li>
              ))}
            </ul>
          </div>
          {errorMessage && (
            <p role="alert" data-test="admin-onboarding-delete-error" className="mt-4 text-sm text-[#BF0002]">
              {errorMessage}
            </p>
          )}
          <div className="mt-6 flex justify-between gap-3">
            <button
              type="button"
              data-test="admin-onboarding-delete-confirm"
              disabled={deleting}
              onClick={onConfirm}
              className={twMerge(
                adminStyles.button.primary,
                adminStyles.button.live,
                adminStyles.button.disabled,
                "w-auto",
              )}
            >
              {deleting ? "Deleting..." : "Delete onboarding"}
            </button>
            <button
              ref={cancelRef}
              type="button"
              data-test="admin-onboarding-delete-cancel"
              disabled={deleting}
              onClick={onClose}
              className={twMerge(
                adminStyles.button.primary,
                adminStyles.button.disabled,
                "w-auto border border-green text-green",
              )}
            >
              Cancel
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default AdminOnboardingDeleteModal;
